import { LanguagesIcon } from "lucide-react";
import { useTranslation } from "@vixi/localization";
import { GoPremiumLink, GoPremiumRoot } from ".";
import { useNavigate } from "@vixi/router";
import {
  Button,
  cn,
  ListItemIcon,
  ListItemLink,
  ListItemText,
  Img,
  listItemLinkVariants,
  useUiStore,
} from "@vixi/ui";
import { useAuthStore, useUserInfo } from "@vixi/api";

// Assets
import NewActivityIcon from "@assets/icons/new-activity.svg?react";
import PersonIcon from "@assets/icons/person.svg?react";
import EmojiIcon from "@assets/icons/emoji.svg?react";
import DashboardOutlinedIcon from "@assets/icons/dashboard.svg?react";
import LogoutIcon from "@assets/icons/logout.svg?react";
import MyCoursesIcon from "@assets/icons/columns-3.svg?react";
import FeedbackIcon from "@assets/icons/feedback.svg?react";
import InstagramIcon from "@assets/icons/instagram.svg?react";
import SettingsOutlineIcon from "@assets/icons/setting-outline.svg?react";
import LogoCompact from "@assets/sidebar-brand-compact.png";
import GoPremiumIcon from "@assets/icons/go-premium.svg?react";

const Sidebar = () => {
  const { t, tFile, switchLanguage, isRtl, dir } = useTranslation();

  const { width, mode, transitionEnded, disabled } = useUiStore((s) => s.sidebar);

  const { logout } = useAuthStore();
  const navigate = useNavigate();

  const { data: userInfo } = useUserInfo();

  const listItemLinkProps = {
    mode,
    disabled,
  };

  return (
    <div
      className="bg-background-paper sticky top-0 z-41 flex h-screen flex-col justify-between border-e-0 p-0 pb-0.5 transition-all duration-300"
      style={{
        width,
        insetInlineStart: 0,
      }}
      dir={dir}
      onTransitionEnd={transitionEnded}
    >
      {/* Header Section */}
      <div className="box-border h-25 border-b-2 border-black/16 p-0 text-center">
        {/* SidebarBrand Component */}
        {mode === "compact" ? (
          <img src={LogoCompact} className="mx-auto h-full w-[37px] object-contain" />
        ) : (
          <img
            src={tFile("/images/logos/", "sidebar-brand.svg")}
            className="h-full w-full object-contain"
          />
        )}
      </div>

      {/* User Info Section */}
      <div className="mx-4 mt-5 flex items-center gap-2.5 text-start">
        <Img className="size-11.5 rounded-full object-cover" src="/images/mocks/profile_photo.png" />
        <div className={mode === "compact" ? "hidden" : "block"}>
          <p className="ar:font-500 font-bold text-black">{userInfo?.firstName}</p>
        </div>
      </div>

      {/* Navigation List */}
      <div
        className={cn(
          "mt-5 flex flex-col gap-2.5 self-stretch",
          mode === "compact" ? "px-4.25 py-0.5" : "px-3",
        )}
      >
      {/*  
        <div>
          <ListItemLink {...listItemLinkProps} to="/dashboard">
            <ListItemIcon>
              <DashboardOutlinedIcon />
            </ListItemIcon>
            <ListItemText mode={mode}>{t("Sidebar.Dashboard")}</ListItemText>
          </ListItemLink>
        </div>
        
        */} 

        <div>
          <ListItemLink {...listItemLinkProps} to="/create">
            <ListItemIcon>
              <NewActivityIcon />
            </ListItemIcon>
            <ListItemText mode={mode}>{t("Sidebar.CreateCourse")}</ListItemText>
          </ListItemLink>
        </div>

        <div>
          <ListItemLink {...listItemLinkProps} to="/avatars">
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText mode={mode}>{t("Sidebar.MyAvatars")}</ListItemText>
          </ListItemLink>
        </div>

        <div>
          <ListItemLink {...listItemLinkProps} to="/courses">
            <ListItemIcon>
              <MyCoursesIcon />
            </ListItemIcon>
            <ListItemText mode={mode}>{t("Sidebar.MyCourses")}</ListItemText>
          </ListItemLink>
        </div>

        <div>
          <ListItemLink {...listItemLinkProps} to="/marketing-kit">
            <ListItemIcon>
              <InstagramIcon />
            </ListItemIcon>
            <ListItemText mode={mode}>{t("Sidebar.MarketingKit")}</ListItemText>
          </ListItemLink>
        </div>
      </div>

      <div
        className={cn(
          "mt-auto flex flex-col gap-2.5 self-stretch border-t-2 border-black/16 py-2",
          mode === "compact" ? "px-4.25" : "px-3",
        )}
      >
        <div>
          <ListItemLink {...listItemLinkProps} to="/chat">
            <ListItemIcon>
              <EmojiIcon />
            </ListItemIcon>
            <ListItemText mode={mode}>{t("Sidebar.ChatWithUs")}</ListItemText>
          </ListItemLink>
        </div>

        <div>
          <ListItemLink {...listItemLinkProps} to="/feedback">
            <ListItemIcon>
              <FeedbackIcon />
            </ListItemIcon>
            <ListItemText mode={mode}>{t("Sidebar.RequestFeature")}</ListItemText>
          </ListItemLink>
        </div>

        <div>
          <ListItemLink {...listItemLinkProps} to="/settings/account-settings">
            <ListItemIcon>
              <SettingsOutlineIcon />
            </ListItemIcon>
            <ListItemText mode={mode}>{t("Sidebar.Settings")}</ListItemText>
          </ListItemLink>
        </div>

        <div>
          <button
            className={cn(listItemLinkVariants())}
            {...listItemLinkProps}
            onClick={() => {
              logout();
              navigate({ to: "/login" });
            }}
          >
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText mode={mode}>{t("Action.LogOut")}</ListItemText>
          </button>
        </div>
      </div>

      {/* Go Pro Section */}
      <GoPremiumRoot>
        <GoPremiumLink
          {...listItemLinkProps}
          dir="ltr"
          to="/premium"
          color="premium"
          variant="text"
          activeProps={{ variant: "contained" }}
        >
          <p
            className={cn("ar:text-[12px]/none block text-[25px]/none font-bold", {
              hidden: mode === "compact",
            })}
          >
            {t("Sidebar.GoPremium")}
          </p>
          <GoPremiumIcon className="text-current fs-35" />
        </GoPremiumLink>
      </GoPremiumRoot>

      <div className="flex items-center justify-center py-1.5">
        <div dir={isRtl ? "ltr" : "rtl"}>
          {mode === "expanded" ? (
            <Button
              onClick={switchLanguage}
              startIcon={<LanguagesIcon />}
              variant="text"
              size="small"
            >
              {t("Sidebar.OtherLanguage")}
            </Button>
          ) : (
            <Button size="icon" onClick={switchLanguage}>
              <LanguagesIcon />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
